import React, { useState, useCallback } from 'react';
import { useNotification } from './Notification';
import { getApiUrl } from '../config';
import { logger } from '../utils/logger';
import { theme } from '../theme';
import { CustomTag } from '../types';

interface CreateCustomTagModalProps {
  isVisible: boolean;
  onClose: () => void;
  onTagCreated: (tag: CustomTag) => void;
  playerPosition?: string;
}

export const CreateCustomTagModal: React.FC<CreateCustomTagModalProps> = ({
  isVisible,
  onClose,
  onTagCreated,
  playerPosition
}) => {
  const { showNotification } = useNotification();
  const [tagName, setTagName] = useState('');
  const [tagType, setTagType] = useState<'POSITIVE' | 'NEGATIVE'>('POSITIVE');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<{ positive: string[]; negative: string[] }>({ positive: [], negative: [] });

  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(`/api/reviews/tags/suggestions/${playerPosition}`));
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      logger.error('Error fetching tag suggestions:', error);
    }
  }, [playerPosition]);

  React.useEffect(() => {
    if (isVisible && playerPosition) {
      fetchSuggestions();
    }
  }, [isVisible, playerPosition, fetchSuggestions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tagName.trim()) {
      showNotification('Debes ingresar un nombre para el tag', 'error');
      return;
    }

    if (tagName.length < 3) {
      showNotification('El tag debe tener al menos 3 caracteres', 'error');
      return;
    }

    if (tagName.length > 50) {
      showNotification('El tag no puede exceder 50 caracteres', 'error');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/reviews/custom-tags'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: tagName.trim(),
          type: tagType
        })
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, 'success');
        onTagCreated({
          name: data.tag.name,
          type: data.tag.type,
          usageCount: data.tag.usageCount
        });
        setTagName('');
        onClose();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error creando el tag', 'error');
      }
    } catch (error) {
      logger.error('Error creating custom tag:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string, type: 'POSITIVE' | 'NEGATIVE') => {
    setTagName(suggestion);
    setTagType(type);
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.card,
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            margin: 0,
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
            fontSize: '18px',
            fontWeight: theme.fonts.weightBold
          }}>
            🏷️ Crear Nuevo Tag Personalizado
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tipo de Tag */}
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="custom-tag-type" style={{
              display: 'block',
              marginBottom: '8px',
              color: theme.colors.text,
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              fontWeight: theme.fonts.weightBold
            }}>
              Tipo de Tag:
            </label>
            <select
              id="custom-tag-type"
              value={tagType}
              onChange={(e) => setTagType(e.target.value as 'POSITIVE' | 'NEGATIVE')}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: theme.borderRadius.input,
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontSize: '14px'
              }}
            >
              <option value="POSITIVE">✅ Positivo</option>
              <option value="NEGATIVE">❌ Negativo</option>
            </select>
          </div>

          {/* Nombre del Tag */}
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="custom-tag-name" style={{
              display: 'block',
              marginBottom: '8px',
              color: theme.colors.text,
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              fontWeight: theme.fonts.weightBold
            }}>
              Nombre del Tag:
            </label>
            <input
              id="custom-tag-name"
              type="text"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Ej: Tiro libre perfecto"
              maxLength={50}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: theme.borderRadius.input,
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontSize: '14px'
              }}
            />
            <div style={{
              fontSize: '12px',
              color: theme.colors.textSecondary,
              marginTop: '4px',
              textAlign: 'right'
            }}>
              {tagName.length}/50
            </div>
          </div>

          {/* Sugerencias por posición */}
          {playerPosition && (suggestions.positive.length > 0 || suggestions.negative.length > 0) && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{
                margin: '0 0 12px 0',
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                fontWeight: theme.fonts.weightBold
              }}>
                💡 Sugerencias para {playerPosition}:
              </h4>
              
              {/* Sugerencias positivas */}
              {suggestions.positive.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '12px',
                    color: theme.colors.success,
                    marginBottom: '6px',
                    fontWeight: theme.fonts.weightBold
                  }}>
                    Positivos:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {suggestions.positive.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion, 'POSITIVE')}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: 'transparent',
                          border: `1px solid ${theme.colors.success}`,
                          borderRadius: '12px',
                          color: theme.colors.success,
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontFamily: theme.fonts.body
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sugerencias negativas */}
              {suggestions.negative.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '12px',
                    color: theme.colors.danger,
                    marginBottom: '6px',
                    fontWeight: theme.fonts.weightBold
                  }}>
                    Negativos:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {suggestions.negative.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion, 'NEGATIVE')}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: 'transparent',
                          border: `1px solid ${theme.colors.danger}`,
                          borderRadius: '12px',
                          color: theme.colors.danger,
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontFamily: theme.fonts.body
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: 'transparent',
                color: theme.colors.textSecondary,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !tagName.trim()}
              style={{
                padding: '10px 20px',
                backgroundColor: theme.colors.primary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                fontWeight: theme.fonts.weightBold,
                cursor: (loading || !tagName.trim()) ? 'not-allowed' : 'pointer',
                opacity: (loading || !tagName.trim()) ? 0.6 : 1
              }}
            >
              {loading ? 'Creando...' : 'Crear Tag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 